var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/* eslint-disable react/function-component-definition */
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Button from '@mui/material/Button';
import CameraSwitchIcon from '@mui/icons-material/Cameraswitch';
import { prepareQrReader, readQrText } from './zxing';
import { parseScreenQr } from './screenQr';
const SCAN_WIDTH = 960;
const DARK_BRIGHTNESS = 35;
const HINT_DARK = 'Слишком темно, добавьте света';
const HINT_NOT_SCREEN = 'Это не QR-код экрана';
const Frame = styled.div `
  position: relative;
  box-sizing: border-box;
  width: 100%;
  aspect-ratio: 4 / 3;
  border-radius: 12px;
  background: #161719;
  overflow: hidden;
`;
const Video = styled.video `
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;
const Target = styled.div `
  position: absolute;
  top: 50%;
  left: 50%;
  width: 60%;
  aspect-ratio: 1;
  max-height: 80%;
  transform: translate(-50%, -50%);
  border: 3px solid #ffffff;
  border-radius: 16px;
  opacity: 0.9;
  pointer-events: none;
`;
const Overlay = styled.div `
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 12px 56px 12px 16px;
  font-size: 14px;
  line-height: 1.4;
  color: #ffffff;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
`;
const Message = styled.div `
  position: absolute;
  inset: 0;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  text-align: center;
  font-size: 16px;
  line-height: 1.5;
  color: #c4cdd0;
`;
const Warning = styled.div `
  font-size: 14px;
  color: #899295;
`;
const switchButtonSx = {
    position: 'absolute',
    right: 8,
    bottom: 8,
    minWidth: 40,
    width: 40,
    height: 40,
    padding: 0,
    background: 'rgba(59, 63, 66, 0.85)',
    color: '#FFFFFF',
    '&:hover': { background: '#4A4F53' },
};
const retryButtonSx = {
    background: '#1976D2',
    color: '#FFFFFF',
    '&:hover': { background: '#1868B7' },
};
const getBrightness = ({ data }) => {
    let total = 0;
    // every 16th pixel is enough for a darkness hint
    for (let i = 0; i < data.length; i += 64)
        total += (data[i] + data[i + 1] + data[i + 2]) / 3;
    return total / (data.length / 64);
};
export const isCameraAvailable = () => { var _a; return typeof window !== 'undefined' && window.isSecureContext && Boolean((_a = navigator.mediaDevices) === null || _a === void 0 ? void 0 : _a.getUserMedia); };
export const ScreenQrScanner = ({ onDetected }) => {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const onDetectedRef = useRef(onDetected);
    onDetectedRef.current = onDetected;
    const [facing, setFacing] = useState('environment');
    const [attempt, setAttempt] = useState(0);
    const [isStarted, setStarted] = useState(false);
    const [hint, setHint] = useState(null);
    const [error, setError] = useState(null);
    const hasCamera = isCameraAvailable();
    useEffect(() => {
        if (!hasCamera)
            return undefined;
        let stopped = false;
        let stream = null;
        let raf = 0;
        let frame = 0;
        let busy = false;
        const stop = () => {
            stopped = true;
            cancelAnimationFrame(raf);
            stream === null || stream === void 0 ? void 0 : stream.getTracks().forEach((t) => t.stop());
        };
        const scan = () => __awaiter(void 0, void 0, void 0, function* () {
            const video = videoRef.current;
            const canvas = canvasRef.current;
            const ctx = canvas === null || canvas === void 0 ? void 0 : canvas.getContext('2d', { willReadFrequently: true });
            if (stopped || !video || !canvas || !ctx)
                return;
            raf = requestAnimationFrame(scan);
            // decode every other frame and never two frames at once
            frame = (frame + 1) % 2;
            if (frame !== 0 || busy || video.readyState < video.HAVE_CURRENT_DATA || !video.videoWidth)
                return;
            busy = true;
            try {
                const scale = Math.min(1, SCAN_WIDTH / video.videoWidth);
                canvas.width = Math.floor(video.videoWidth * scale);
                canvas.height = Math.floor(video.videoHeight * scale);
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const text = yield readQrText(image);
                if (stopped)
                    return;
                if (!text) {
                    setHint(getBrightness(image) < DARK_BRIGHTNESS ? HINT_DARK : null);
                    return;
                }
                const code = parseScreenQr(text);
                if (!code) {
                    setHint(HINT_NOT_SCREEN);
                    return;
                }
                stop();
                onDetectedRef.current(code);
            }
            catch (e) {
                // a broken frame is not fatal, the next one will be decoded
            }
            finally {
                busy = false;
            }
        });
        const start = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                setError(null);
                setHint(null);
                yield prepareQrReader();
                stream = yield navigator.mediaDevices.getUserMedia({
                    video: { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 720 } },
                    audio: false,
                });
                const video = videoRef.current;
                if (stopped || !video) {
                    stop();
                    return;
                }
                video.srcObject = stream;
                yield video.play();
                setStarted(true);
                scan();
            }
            catch (e) {
                if (stopped)
                    return;
                const denied = e instanceof DOMException && e.name === 'NotAllowedError';
                setError(denied ? 'Нет доступа к камере. Разрешите его в настройках браузера' : 'Не удалось запустить камеру');
            }
        });
        setStarted(false);
        start();
        return stop;
    }, [facing, attempt, hasCamera]);
    const onRetry = () => setAttempt((n) => n + 1);
    if (!hasCamera) {
        return (_jsx(Frame, { children: _jsxs(Message, { children: ["\u041A\u0430\u043C\u0435\u0440\u0430 \u0434\u043E\u0441\u0442\u0443\u043F\u043D\u0430 \u0442\u043E\u043B\u044C\u043A\u043E \u043F\u043E HTTPS", _jsx(Warning, { children: "\u0412\u0432\u0435\u0434\u0438\u0442\u0435 \u043A\u043E\u0434 \u0441 \u044D\u043A\u0440\u0430\u043D\u0430 \u0432\u0440\u0443\u0447\u043D\u0443\u044E" })] }) }));
    }
    return (_jsxs(Frame, { children: [_jsx(Video, { ref: videoRef, playsInline: true, muted: true }), _jsx("canvas", { ref: canvasRef, hidden: true }), isStarted && _jsx(Target, {}), !isStarted && !error && _jsx(Message, { children: "\u0412\u043A\u043B\u044E\u0447\u0430\u0435\u043C \u043A\u0430\u043C\u0435\u0440\u0443\u2026" }), error && (_jsxs(Message, Object.assign({ role: "alert" }, { children: [error, _jsx(Button, Object.assign({ sx: retryButtonSx, onClick: onRetry }, { children: "\u041F\u043E\u043F\u0440\u043E\u0431\u043E\u0432\u0430\u0442\u044C \u0435\u0449\u0451 \u0440\u0430\u0437" }))] }))), isStarted && !error && (_jsxs(_Fragment, { children: [_jsx(Overlay, { children: hint || 'Наведите камеру на QR-код на экране' }), _jsx(Button, Object.assign({ "aria-label": "\u041F\u0435\u0440\u0435\u043A\u043B\u044E\u0447\u0438\u0442\u044C \u043A\u0430\u043C\u0435\u0440\u0443", sx: switchButtonSx, onClick: () => setFacing((f) => (f === 'environment' ? 'user' : 'environment')) }, { children: _jsx(CameraSwitchIcon, {}) }))] }))] }));
};
